import { AfterContentInit, Component, OnChanges, SimpleChanges } from "@angular/core";
import { BaseCalculatorComponent } from "../../calculator/base/base-calculator.component";
import { ActivatedRoute } from "@angular/router";
import { AppBarTitleService, HorizontalListData, OfferCalculatorService, OfferTermLoanDetailsAdapter } from "common";
import { DomSanitizer } from "@angular/platform-browser";
import { AppSettings } from "../../../app.settings";
import { CurrencyPipe, PercentPipe } from "@angular/common";
import { ApplicationService } from "../application.service";
import { ChartConfiguration } from "chart.js";
import { getBarChartData, getBarChartOptions } from "./term-loan-bar-chart-data";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
    selector: "ifa-term-loan-content",
    templateUrl: "./term-loan-content.component.html",
    styleUrls: ["./term-loan-content.component.scss"],
    providers: [OfferTermLoanDetailsAdapter],
    standalone: false
})
export class TermLoanContentComponent
  extends BaseCalculatorComponent
  implements OnChanges, AfterContentInit {

  private _chartData: ChartData = null;

  private _barChartData: ChartConfiguration<"bar">["data"] = null;
  private _barChartOptions: ChartConfiguration<"bar">["options"] = null;

  private _amount: number;

  showSlider: boolean = false;
  detailsListItems: HorizontalListData[];

  constructor(
    route: ActivatedRoute,
    sanitizer: DomSanitizer,
    appBarTitleService: AppBarTitleService,
    protected calculatorService: OfferCalculatorService,
    applicationService: ApplicationService,
    settings: AppSettings,
    percentPipe: PercentPipe,
    currencyPipe: CurrencyPipe,
    private readonly adapter: OfferTermLoanDetailsAdapter
  ) {
    super(
      route,
      sanitizer,
      appBarTitleService,
      calculatorService,
      applicationService,
      settings,
      percentPipe,
      currencyPipe
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    this._barChartData = null;
    this._barChartOptions = null;

    super.ngOnChanges(changes);
  }

  ngAfterContentInit(): void {
    super.ngAfterContentInit();
    this.showSlider = this.offerCalculator.minAmount < this.offerCalculator.maxAmount;
    this._amount = this.offerCalculator.amount;
    this.initListData();
    this.calculatorService.inputDataChanged$.pipe(untilDestroyed(this))
      .subscribe(() => {
        this.initListData();
      })
  }

  private initListData(): void {
    this.detailsListItems = this.adapter.adaptDetailsListData(this.offerCalculator, this.customerView || this.isRenewal);
  }

  get barChartData(): any {
    if (!this._barChartData) {
      this._barChartData = this.prepareBarChartData();
    }

    return this._barChartData;
  }

  get barChartOptions(): any {
    if (!this._barChartOptions) {
      this._barChartOptions = this.prepareBarChartOptions();
    }

    return this._barChartOptions;
  }

  get amount(): number {
    return this._amount;
  }

  set amount(amount: number) {
    this._amount = amount;
    this.offerCalculator.amount = amount;
  }

  onAmountBlur(): void {
    if (this._amount > this.offerCalculator.maxAmount)
      this._amount = this.offerCalculator.maxAmount;
    else if (this._amount < this.offerCalculator.minAmount)
      this._amount = this.offerCalculator.minAmount;

    this._barChartData = this.prepareBarChartData();
  }

  onChange(): void {
    this._barChartData = this.prepareBarChartData();
  }

  onAmountDisplay(value: number): string {
    return Math.round(value / 1000).toString() + 'K';
  }

  showDisclosures(): boolean {
    window.open(`/disclosures?uuid=${this.offerCalculator.application?.uuid}&offerId=${this.offerCalculator.applicationOffer.id}`, "_blank");
    return false;
  }

  private get chartData(): ChartData {
    this._chartData = {
      labels: [],
      interest: [],
      principal: [],
      total: [],
    };

    const payments = this._calculatorService.getTermLoanPayments(this.offer, null, this.offerCalculator.amount);

    for (let idx = 0; idx < payments.length; idx++) {
      this._chartData.labels.push(payments[idx].label);
      this._chartData.interest.push(payments[idx]?.interestBalance);
      this._chartData.principal.push(payments[idx]?.principalBalance);
      this._chartData.total.push(
        payments[idx]?.interestBalance + payments[idx]?.principalBalance
      );
    }

    return this._chartData;
  }

  private prepareBarChartData(): ChartConfiguration<"bar">["data"] {
    return getBarChartData(this.chartData.total, this.chartData.labels);
  }

  private prepareBarChartOptions(): ChartConfiguration<"bar">["options"] {
    return getBarChartOptions;
  }
}

class ChartData {
  labels: string[];
  interest: number[];
  principal: number[];
  total: number[];
}
